import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Button, Col, Divider, Input, InputNumber, Modal, Radio, Row, Select, Space, Table, Tag, Typography, message
} from "antd";
import {
    fetchSettlementExtras,
    createSettlement,
    listFinancialYears
} from "../../actions/settlement.action";
import { getWorkFlowAction } from "../../actions/reports.action";
import { getActiveOperatorsForSettlement } from "../../actions/admin.action";
import { buildOperatorDrafts, computeTotals } from "../../utils/settlement-lines.util";
import { showConfirm } from "../../components/custom-components/custom-component";
import useColumnSearch from "../../ui-components/table-column-search";

const { Title, Text } = Typography;
const { Option } = Select;
const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

// Months ordered as they appear in an Indian financial year (Apr first)
const FY_MONTH_OPTIONS = [
    { num: 4,  name: "April"     }, { num: 5,  name: "May"       },
    { num: 6,  name: "June"      }, { num: 7,  name: "July"      },
    { num: 8,  name: "August"    }, { num: 9,  name: "September" },
    { num: 10, name: "October"   }, { num: 11, name: "November"  },
    { num: 12, name: "December"  }, { num: 1,  name: "January"   },
    { num: 2,  name: "February"  }, { num: 3,  name: "March"     }
];

// Week dropdown options 1..count. `count` is computed per month/weekday
// (see weeksInFYMonth) so months whose tail spills into a 6th bucket expose
// Week 6 — otherwise those trailing days would be unreachable.
const buildWeekOptions = (count) =>
    Array.from({ length: Math.max(1, count) }, (_, i) => ({ num: i + 1, label: `Week ${i + 1}` }));

// ISO weekday: 1 = Monday ... 7 = Sunday.
const WEEKDAY_ISO = {
    MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4,
    FRIDAY: 5, SATURDAY: 6, SUNDAY: 7
};

// Plain Indian-grouped 2-decimal number (e.g. 12,34,567.89) — for quantities.
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Money: ₹ + Indian grouping, sign kept before the symbol (e.g. -₹1,234.00).
const inr = (n) => {
    const num = Number(n || 0);
    return (num < 0 ? "-₹" : "₹") + fmtNum(Math.abs(num));
};
const pad2 = (n) => String(n).padStart(2, "0");

// Indian FY = Apr 1 of YYYY -> Mar 31 of YYYY+1.  Returns "YYYY-YY" string.
const deriveCurrentFY = () => {
    const now = new Date();
    const startYear = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

// Calendar year for a (FY, month). Months 4..12 -> FY's start year; 1..3 -> +1.
const yearForFYMonth = (financialYear, month) => {
    const start = parseInt(String(financialYear).split("-")[0], 10);
    return month >= 4 ? start : start + 1;
};

const daysInMonth = (year, month /* 1-12 */) => new Date(year, month, 0).getDate();

// Compute periodStart/periodEnd from cycle + (financialYear, month, week, weekday).
// WEEKLY rule:
//   Week 1 of the month starts on the most-recent occurrence of the selected
//   weekday on-or-before day 1 of the month and runs for 7 days. Week N is
//   shifted by (N-1)*7 days. The resulting [start, end] is clipped to the
//   selected month, so cross-month days belong to the adjacent month's week.
//   Example: May 2026 with Monday → W1 raw = Apr 27 – May 3, clipped = May 1 – 3.
const computePeriod = (cycle, financialYear, month, week, weekday) => {
    if (!financialYear || !month) return { periodStart: null, periodEnd: null };
    const year = yearForFYMonth(financialYear, month);
    const dim = daysInMonth(year, month);
    const ms = pad2(month);

    if (cycle === "MONTHLY") {
        return { periodStart: `${year}-${ms}-01`, periodEnd: `${year}-${ms}-${pad2(dim)}` };
    }
    const targetIso = WEEKDAY_ISO[(weekday || "MONDAY").toUpperCase()];
    if (!targetIso) return { periodStart: null, periodEnd: null };
    const w = week || 1;

    // Convert JS Date.getDay() (0=Sun..6=Sat) to ISO (1=Mon..7=Sun).
    const day1 = new Date(year, month - 1, 1);
    const day1Iso = ((day1.getDay() + 6) % 7) + 1;
    const backDays = (day1Iso - targetIso + 7) % 7;
    const week1Start = new Date(year, month - 1, 1 - backDays);
    const rawStart = new Date(week1Start);
    rawStart.setDate(week1Start.getDate() + 7 * (w - 1));
    const rawEnd = new Date(rawStart);
    rawEnd.setDate(rawStart.getDate() + 6);

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month - 1, dim);
    if (rawEnd < monthStart || rawStart > monthEnd) {
        return { periodStart: null, periodEnd: null };
    }
    const clippedStart = rawStart < monthStart ? monthStart : rawStart;
    const clippedEnd = rawEnd > monthEnd ? monthEnd : rawEnd;
    const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return { periodStart: fmt(clippedStart), periodEnd: fmt(clippedEnd) };
};

// How many weekday-aligned weeks are needed to fully cover the month.
// Week 1 starts on the most-recent settlement-weekday on-or-before day 1, so
// when day 1 sits late in its bucket the month's last days spill into a 6th
// week. Corner case: June with a Tuesday settlement day when June 1 is a Monday
// → week 1 starts May 26, and June 30 lands in week 6. Returns 4–6.
const weeksInFYMonth = (financialYear, month, weekday) => {
    if (!financialYear || !month) return 5;
    const year = yearForFYMonth(financialYear, month);
    const dim = daysInMonth(year, month);
    const targetIso = WEEKDAY_ISO[(weekday || "MONDAY").toUpperCase()];
    if (!targetIso) return 5;
    const day1 = new Date(year, month - 1, 1);
    const day1Iso = ((day1.getDay() + 6) % 7) + 1;
    const backDays = (day1Iso - targetIso + 7) % 7;
    const week1Start = new Date(year, month - 1, 1 - backDays);
    const monthEnd = new Date(year, month - 1, dim);
    const dayDiff = Math.round((monthEnd - week1Start) / 86400000);
    return Math.floor(dayDiff / 7) + 1;
};

export const SettlementsGeneratePage = () => {
    // --- filters ---
    const [cycle, setCycle] = useState("WEEKLY");
    const [financialYears, setFinancialYears] = useState([]);
    const [financialYear, setFinancialYear] = useState(deriveCurrentFY());
    // Default to the current calendar month
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [week, setWeek] = useState(1);
    const [weekday, setWeekday] = useState("MONDAY");
    // Computed period (read-only display + sent on fetch)
    const { periodStart, periodEnd } = useMemo(
        () => computePeriod(cycle, financialYear, month, week, weekday),
        [cycle, financialYear, month, week, weekday]
    );

    // Week options depend on month/weekday so the 6th-week corner case is
    // selectable. Clamp the current selection if the month shrinks to fewer weeks.
    const weekOptions = useMemo(
        () => buildWeekOptions(weeksInFYMonth(financialYear, month, weekday)),
        [financialYear, month, weekday]
    );
    useEffect(() => {
        if (week > weekOptions.length) setWeek(weekOptions.length);
    }, [weekOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- workflow data + computed drafts ---
    const [loading, setLoading] = useState(false);
    const [workflow, setWorkflow] = useState(null);
    const [drafts, setDrafts] = useState([]);

    // --- per-draft local edits + draft modal ---
    // A draft is keyed as `operator_code::kind` so a single operator can have
    // multiple drafts (FULL, WEB_ONLY, OTHER_ONLY) edited independently.
    const [editsByOp, setEditsByOp] = useState({});  // { [draftKey]: { lines, openingBalance } }
    const [savingOp, setSavingOp] = useState(null);
    const [modalOpenFor, setModalOpenFor] = useState(null);  // draftKey

    // Set of `operator_code::kind::periodStart::periodEnd` keys for which a
    // NON-cancelled settlement already exists in the selected time frame. Used
    // to block re-generating (a cancelled settlement can be regenerated).
    const [existingKeys, setExistingKeys] = useState(new Set());

    const draftKeyOf = (d) => `${d.operator.operator_code}::${d.kind || 'FULL'}`;

    // Timeframe-scoped key for the existing-settlement guard. Period comes from
    // the current selection (the list is always for one period), normalised to
    // YYYY-MM-DD on both sides so string/ISO shapes compare cleanly.
    const day10 = (x) => String(x || "").slice(0, 10);
    const blockKeyOf = (operatorCode, kind) =>
        `${operatorCode}::${kind || 'FULL'}::${day10(periodStart)}::${day10(periodEnd)}`;

    // Load financial-year list on mount; default already set to current FY
    useEffect(() => {
        listFinancialYears(
            (data) => setFinancialYears(Array.isArray(data) ? data : []),
            (errMsg) => message.error(errMsg)
        );
    }, []);

    /**
     * Workflow data is sourced from THREE separate places:
     *   1. travels server: getActiveOperatorsForSettlement(cycle, weekday) — operators + config, filtered server-side by cycle/weekday
     *   2. travels server: getWorkFlowAction(start, end)   — pre-aggregated activity totals
     *   3. admin_manager_server: fetchSettlementExtras(...) — FY + openings + prior outstandings
     */
    const onFetch = () => {
        if (!periodStart || !periodEnd) {
            message.warning("Selected month/week/weekday combination has no days inside the chosen month. Adjust your selection.");
            return;
        }
        setLoading(true);
        setEditsByOp({});
        setDrafts([]);
        setExistingKeys(new Set());

        const opsP = new Promise((res, rej) =>
            getActiveOperatorsForSettlement(
                cycle,
                cycle === "WEEKLY" ? weekday : null,
                (data) => res(data),
                (err) => rej(err)
            ));
        const summaryP = new Promise((res, rej) =>
            getWorkFlowAction(periodStart, periodEnd, (data) => res(data), (err) => rej(err)));

        Promise.all([opsP, summaryP])
            .then(([operators, workflowSummary]) => {
                const operatorCodes = operators.map((o) => o.operator_code);
                fetchSettlementExtras(
                    { periodStart, periodEnd, operatorCodes },
                    (extras) => {
                        const stitched = {
                            period: extras.period,
                            financialYear: extras.financialYear,
                            openingBalances: extras.openingBalances,
                            priorOutstandings: extras.priorOutstandings,
                            operators,
                            workflowSummary
                        };
                        setWorkflow(stitched);
                        // Server already returns only NON-cancelled settlements for
                        // this exact period (extras.existingSettlements) — no extra
                        // client round-trip. Build the exclusion set from those.
                        const blocked = new Set();
                        (extras.existingSettlements || []).forEach((s) => {
                            blocked.add(`${s.operator_code}::${s.kind || 'FULL'}::${day10(periodStart)}::${day10(periodEnd)}`);
                        });
                        setExistingKeys(blocked);
                        const built = buildOperatorDrafts(stitched, cycle);
                        // Drop operators that already have a NON-cancelled settlement
                        // for this exact time frame — only cancelled / not-yet-generated
                        // operators remain generatable. A cancelled one is regeneratable.
                        const visible = built.filter(
                            (d) => !blocked.has(`${d.operator.operator_code}::${d.kind || 'FULL'}::${day10(periodStart)}::${day10(periodEnd)}`)
                        );
                        setDrafts(visible);
                        setLoading(false);
                    },
                    (errMsg) => {
                        setLoading(false);
                        message.error(errMsg);
                    }
                );
            })
            .catch((errMsg) => {
                setLoading(false);
                message.error(typeof errMsg === "string" ? errMsg : "Failed to load workflow data");
            });
    };

    // ---- merge: original draft for (operator, kind) + any local edits ----
    const getEditedDraft = (draftKey) => {
        const base = drafts.find((d) => draftKeyOf(d) === draftKey);
        if (!base) return null;
        const ed = editsByOp[draftKey] || {};
        const lines = ed.lines || base.lines;
        const openingBalance = ed.openingBalance != null ? ed.openingBalance : base.openingBalance;
        const totals = computeTotals(lines, openingBalance);
        const isOverridden = !!ed.lines || ed.openingBalance != null;
        return { ...base, lines, openingBalance, totals, isOverridden };
    };

    // ---- modal-side editing ----
    // Recompute charge_amount from qty * unit (or qty * unit / 100 for percent units),
    // for any line that opts in via auto_compute or is_custom.
    const deriveChargeIfAuto = (line) => {
        const auto = line.auto_compute || line.is_custom;
        if (!auto) return line.charge_amount;
        const qty = Number(line.quantity);
        const unit = Number(line.unit_price);
        if (!Number.isFinite(qty) || !Number.isFinite(unit)) return line.charge_amount;
        const raw = line.unit_is_percent ? (qty * unit / 100) : (qty * unit);
        return Math.round(raw * 100) / 100;
    };

    const updateLineAmount = (draftKey, idx, field, value) => {
        const current = getEditedDraft(draftKey);
        if (!current) return;
        const newLines = current.lines.map((l, i) => i === idx ? { ...l, [field]: Number(value) || 0 } : l);
        setEditsByOp((prev) => ({
            ...prev,
            [draftKey]: { ...(prev[draftKey] || {}), lines: newLines }
        }));
    };

    // Edit description / quantity / unit_price on lines (auto-recompute charge if applicable)
    const updateLineField = (draftKey, idx, field, value) => {
        const current = getEditedDraft(draftKey);
        if (!current) return;
        const newLines = current.lines.map((l, i) => {
            if (i !== idx) return l;
            const updated = { ...l, [field]: value };
            if (field === "quantity" || field === "unit_price") {
                updated.charge_amount = deriveChargeIfAuto(updated);
            }
            return updated;
        });
        setEditsByOp((prev) => ({
            ...prev,
            [draftKey]: { ...(prev[draftKey] || {}), lines: newLines }
        }));
    };

    const addCustomLine = (draftKey) => {
        const current = getEditedDraft(draftKey);
        if (!current) return;
        const newLine = {
            line_code: "CUSTOM",
            description: "",
            quantity: null,
            unit_price: null,
            charge_amount: 0,
            credit_amount: 0,
            is_custom: true
        };
        const newLines = [...current.lines, newLine];
        setEditsByOp((prev) => ({
            ...prev,
            [draftKey]: { ...(prev[draftKey] || {}), lines: newLines }
        }));
    };

    const removeLine = (draftKey, idx) => {
        const current = getEditedDraft(draftKey);
        if (!current) return;
        const newLines = current.lines.filter((_, i) => i !== idx);
        setEditsByOp((prev) => ({
            ...prev,
            [draftKey]: { ...(prev[draftKey] || {}), lines: newLines }
        }));
    };

    const updateOpeningBalance = (draftKey, value) => {
        setEditsByOp((prev) => ({
            ...prev,
            [draftKey]: { ...(prev[draftKey] || {}), openingBalance: Number(value) || 0 }
        }));
    };

    const resetEdits = (draftKey) => {
        setEditsByOp((prev) => {
            const next = { ...prev };
            delete next[draftKey];
            return next;
        });
    };

    // ---- persist ----
    const onGenerate = (draftKey, status) => {
        const d = getEditedDraft(draftKey);
        if (!d) return;
        if (!workflow || !workflow.financialYear) {
            message.error("Workflow data missing — please re-fetch");
            return;
        }
        // Block regenerating when a non-cancelled settlement already exists for
        // this operator + kind in the same time frame. Only a cancelled one
        // may be regenerated.
        if (existingKeys.has(blockKeyOf(d.operator.operator_code, d.kind))) {
            message.warning(
                `A settlement for ${d.operator.name} already exists for this period and is not cancelled. Cancel it first to regenerate.`
            );
            return;
        }
        const linesForServer = d.lines.map((l, idx) => ({
            ...l,
            auto_quantity: l.auto_quantity == null ? l.quantity : l.auto_quantity,
            auto_charge_amount: l.auto_charge_amount == null ? l.charge_amount : l.auto_charge_amount,
            auto_credit_amount: l.auto_credit_amount == null ? l.credit_amount : l.auto_credit_amount,
            sort_order: idx
        }));

        setSavingOp(draftKey);
        createSettlement({
            operatorCode: d.operator.operator_code,
            financialYear: workflow.financialYear.financial_year,
            cycle,
            kind: d.kind || 'FULL',
            periodStart,
            periodEnd,
            openingBalance: d.openingBalance,
            subTotalCharges: d.totals.subTotalCharges,
            subTotalCredits: d.totals.subTotalCredits,
            gstAmount: d.totals.gstAmount,
            invoiceAmount: d.totals.invoiceAmount,
            netSettlement: d.totals.netSettlement,
            isOverridden: d.isOverridden,
            status,
            lines: linesForServer
        }, (resp, msg) => {
            setSavingOp(null);
            message.success(`${msg || "Saved"} (${resp.settlement_no})`);
            // remove just that (operator, kind) row from the list
            setDrafts((prev) => prev.filter((x) => draftKeyOf(x) !== draftKey));
            resetEdits(draftKey);
            setModalOpenFor(null);
        }, (errMsg) => {
            setSavingOp(null);
            message.error(errMsg);
        });
    };

    // Column-header search for operator name (overrides onFilter because
    // dataIndex is nested at ["operator","name"] — the hook's default reads a
    // flat dataIndex; we replace with a nested-path lookup).
    const operatorColumnSearch = useColumnSearch({ dataIndexExt: "operatorName" });

    // ---- list columns ----
    const tableColumns = useMemo(() => [
        {
            title: "Operator",
            dataIndex: ["operator", "name"],
            sorter: (a, b) => (a.operator.name || "").localeCompare(b.operator.name || ""),
            ...operatorColumnSearch,
            onFilter: (value, record) =>
                (record.operator && record.operator.name ? record.operator.name : "")
                    .toLowerCase()
                    .includes(String(value).toLowerCase())
        },
        {
            title: "Op #",
            dataIndex: ["operator", "operator_number"],
            width: 80,
            defaultSortOrder: "ascend",
            sorter: (a, b) =>
                (Number(a.operator.operator_number) || 0) - (Number(b.operator.operator_number) || 0)
        },
        {
            title: "Kind",
            width: 110,
            render: (_, row) => row.kind && row.kind !== "FULL"
                ? <Tag color={row.kind === "WEB_ONLY" ? "blue" : "orange"}>{row.kindLabel}</Tag>
                : <Tag>Full</Tag>
        },
        {
            title: "Opening",
            width: 110,
            render: (_, row) => {
                const d = getEditedDraft(draftKeyOf(row));
                return d ? inr(d.openingBalance) : "—";
            }
        },
        {
            title: "Charges",
            width: 110,
            render: (_, row) => {
                const d = getEditedDraft(draftKeyOf(row));
                return d ? inr(d.totals.invoiceAmount) : "—";
            }
        },
        {
            title: "Credits",
            width: 110,
            render: (_, row) => {
                const d = getEditedDraft(draftKeyOf(row));
                return d ? inr(d.totals.subTotalCredits) : "—";
            }
        },
        {
            title: "Net",
            width: 130,
            render: (_, row) => {
                const d = getEditedDraft(draftKeyOf(row));
                if (!d) return "—";
                const n = d.totals.netSettlement;
                const color = n > 0 ? "#389e0d" : n < 0 ? "#cf1322" : "#8c8c8c";
                const label = n > 0 ? `+ ${inr(n)}` : n < 0 ? `− ${inr(-n)}` : inr(0);
                return <Text strong style={{ color, fontSize: 16 }}>{label}</Text>;
            }
        },
        {
            title: "Actions",
            width: 280,
            render: (_, row) => {
                const dk = draftKeyOf(row);
                const isSavingThisRow = savingOp === dk;
                const kindNote = row.kind && row.kind !== "FULL" ? ` (${row.kindLabel})` : "";
                return (
                    <Space>
                        <Button onClick={() => setModalOpenFor(dk)}>
                            Review &amp; edit
                        </Button>
                        <Button
                            type="primary"
                            loading={isSavingThisRow}
                            onClick={() => showConfirm(
                                () => onGenerate(dk, "DRAFT"),
                                "Generate Draft",
                                `Save settlement for ${row.operator.name}${kindNote} as DRAFT?`
                            )}
                        >
                            Generate Draft
                        </Button>
                        <Button
                            type="primary"
                            danger
                            loading={isSavingThisRow}
                            onClick={() => showConfirm(
                                () => onGenerate(dk, "APPROVED"),
                                "Generate Settlement",
                                `Save and APPROVE settlement for ${row.operator.name}${kindNote}?`,
                                "Approve"
                            )}
                        >
                            Generate Settlement
                        </Button>
                    </Space>
                );
            }
        }
    ], [drafts, editsByOp, savingOp]);

    // ---- modal: editable settlement ----
    const editingDraft = modalOpenFor ? getEditedDraft(modalOpenFor) : null;

    return (
        <div className="settlement-admin" style={{ margin: 20 }}>
            <Title level={4}>Generate Settlements</Title>

            <div style={{ background: "#fafafa", padding: 16, marginBottom: 16, borderRadius: 4 }}>
                <Row gutter={[16, 12]} align="middle" wrap>
                    <Col>
                        <Text strong>Cycle:</Text>
                    </Col>
                    <Col>
                        <Radio.Group value={cycle} onChange={(e) => setCycle(e.target.value)}>
                            <Radio.Button value="WEEKLY">Weekly</Radio.Button>
                            <Radio.Button value="MONTHLY">Monthly</Radio.Button>
                        </Radio.Group>
                    </Col>
                    <Col>
                        <Text strong>FY:</Text>
                    </Col>
                    <Col>
                        <Select
                            value={financialYear}
                            onChange={setFinancialYear}
                            style={{ width: 130 }}
                        >
                            {(financialYears.length > 0
                                ? financialYears.map((fy) => fy.financial_year)
                                : [financialYear]
                            ).map((fy) => (
                                <Option key={fy} value={fy}>{fy}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Text strong>Month:</Text>
                    </Col>
                    <Col>
                        <Select value={month} onChange={setMonth} style={{ width: 140 }}>
                            {FY_MONTH_OPTIONS.map((m) => (
                                <Option key={m.num} value={m.num}>{m.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    {cycle === "WEEKLY" && (
                        <>
                            <Col>
                                <Text strong>Settlement day:</Text>
                            </Col>
                            <Col>
                                <Select value={weekday} onChange={setWeekday} style={{ width: 130 }}>
                                    {WEEKDAYS.map((w) => <Option key={w} value={w}>{w}</Option>)}
                                </Select>
                            </Col>
                            <Col>
                                <Text strong>Week:</Text>
                            </Col>
                            <Col>
                                <Select value={week} onChange={setWeek} style={{ width: 200 }}>
                                    {weekOptions.map((w) => (
                                        <Option key={w.num} value={w.num}>{w.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                        </>
                    )}
                    <Col>
                        <Button type="primary" loading={loading} onClick={onFetch}>
                            Fetch
                        </Button>
                    </Col>
                </Row>
                <Row style={{ marginTop: 8 }}>
                    <Col>
                        <Text type="secondary">Period:&nbsp;</Text>
                        {periodStart && periodEnd
                            ? <Text strong>{periodStart} &nbsp;→&nbsp; {periodEnd}</Text>
                            : <Text type="danger">No valid period for this selection</Text>
                        }
                    </Col>
                </Row>
            </div>

            {workflow && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="info"
                    showIcon
                    message={`FY ${workflow.financialYear && workflow.financialYear.financial_year} — period ${workflow.period.start} to ${workflow.period.end}. ${drafts.length} operators ready.`}
                />
            )}

            <Table
                rowKey={(row) => draftKeyOf(row)}
                dataSource={drafts}
                columns={tableColumns}
                loading={loading}
                pagination={{ pageSize: 25 }}
                scroll={{ x: 1100 }}
                summary={(pageData) => {
                    let weOwe = 0;
                    let toCollect = 0;
                    pageData.forEach((row) => {
                        const d = getEditedDraft(draftKeyOf(row));
                        if (!d) return;
                        const n = d.totals.netSettlement;
                        if (n > 0) weOwe += n;
                        else if (n < 0) toCollect += -n;
                    });
                    return (
                        <Table.Summary.Row style={{ background: "#fafafa" }}>
                            <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            <Table.Summary.Cell index={3}></Table.Summary.Cell>
                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                            <Table.Summary.Cell index={5}></Table.Summary.Cell>
                            <Table.Summary.Cell index={6}>
                                <Text strong style={{ color: "#389e0d", fontSize: 16, display: "block" }}>
                                    + {inr(weOwe)}
                                </Text>
                                <Text strong style={{ color: "#cf1322", fontSize: 16, display: "block" }}>
                                    − {inr(toCollect)}
                                </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7}></Table.Summary.Cell>
                        </Table.Summary.Row>
                    );
                }}
            />

            <Modal
                wrapClassName="settlement-modal"
                visible={!!modalOpenFor}
                onCancel={() => setModalOpenFor(null)}
                width="min(1100px, 95vw)"
                style={{ top: 16 }}
                bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
                title={editingDraft
                    ? (
                        <span>
                            <Text strong style={{ fontSize: 20 }}>{editingDraft.operator.name}</Text>
                            <Text type="secondary">
                                {` — settlement draft${editingDraft.kind && editingDraft.kind !== "FULL" ? ` (${editingDraft.kindLabel})` : ""}`}
                            </Text>
                        </span>
                    )
                    : ""
                }
                footer={editingDraft ? [
                    <Button key="reset" onClick={() => resetEdits(modalOpenFor)}>
                        Reset to auto
                    </Button>,
                    <Button key="close" onClick={() => setModalOpenFor(null)}>
                        Close
                    </Button>,
                    <Button
                        key="draft"
                        type="primary"
                        loading={savingOp === modalOpenFor}
                        onClick={() => onGenerate(modalOpenFor, "DRAFT")}
                    >
                        Generate Draft
                    </Button>,
                    <Button
                        key="settlement"
                        type="primary"
                        danger
                        loading={savingOp === modalOpenFor}
                        onClick={() => showConfirm(
                            () => onGenerate(modalOpenFor, "APPROVED"),
                            "Generate Settlement",
                            "Save and APPROVE this settlement?",
                            "Approve"
                        )}
                    >
                        Generate Settlement
                    </Button>
                ] : null}
            >
                {editingDraft && (
                    <div>
                        <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={8}>
                                <Text strong>Opening Balance (signed): </Text>
                                <InputNumber
                                    value={editingDraft.openingBalance}
                                    onChange={(v) => updateOpeningBalance(modalOpenFor, v)}
                                    style={{ width: "100%" }}
                                    step={1}
                                    precision={2}
                                />
                                <div><Text type="secondary">+ credit (we owe), − debit (they owe)</Text></div>
                                {editingDraft.openingSource && (
                                    <div style={{ marginTop: 4 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {editingDraft.openingSource.kind === "PRIOR_SETTLEMENT"
                                                ? <>Carried forward from <Text strong>{editingDraft.openingSource.settlement_no}</Text>{editingDraft.openingSource.period ? <> ({editingDraft.openingSource.period})</> : null}</>
                                                : <>From FY {editingDraft.openingSource.financial_year} opening balance seed</>
                                            }
                                        </Text>
                                    </div>
                                )}
                            </Col>
                        </Row>
                        <Divider style={{ margin: "12px 0" }} />
                        <Table
                            rowKey={(_, idx) => idx}
                            dataSource={editingDraft.lines}
                            pagination={false}
                            size="small"
                            summary={(rows) => {
                                const tQty    = rows.reduce((s, l) => s + (Number(l.quantity)      || 0), 0);
                                const tCharge = rows.reduce((s, l) => s + (Number(l.charge_amount) || 0), 0);
                                const tCredit = rows.reduce((s, l) => s + (Number(l.credit_amount) || 0), 0);
                                const gst = tCharge * 0.18;
                                const invoiceTotal = tCharge + gst;
                                return (
                                    <>
                                        <Table.Summary.Row style={{ background: "#fafafa" }}>
                                            <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}><Text strong>{fmtNum(tQty)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>—</Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(tCharge)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}><Text strong>{inr(tCredit)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={5}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>GST (18%) on charges</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(gst)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={5}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>Invoice Amount</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong style={{ color: "#cf1322" }}>{inr(invoiceTotal)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={5}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </>
                                );
                            }}
                            columns={[
                                {
                                    title: "Description",
                                    dataIndex: "description",
                                    render: (v, row, idx) => row.is_custom
                                        ? (
                                            <Input
                                                value={v}
                                                onChange={(e) => updateLineField(modalOpenFor, idx, "description", e.target.value)}
                                                placeholder="Custom line description"
                                            />
                                        ) : v
                                },
                                {
                                    title: "Qty",
                                    dataIndex: "quantity",
                                    width: 110,
                                    render: (v, row, idx) => {
                                        const editable = row.is_custom || row.auto_compute;
                                        return editable
                                            ? (
                                                <InputNumber
                                                    value={v}
                                                    onChange={(val) => updateLineField(modalOpenFor, idx, "quantity", val)}
                                                    step={0.01}
                                                    precision={2}
                                                    style={{ width: "100%" }}
                                                />
                                            )
                                            : (v == null ? "—" : fmtNum(v));
                                    }
                                },
                                {
                                    title: "Unit",
                                    dataIndex: "unit_price",
                                    width: 110,
                                    render: (v, row, idx) => {
                                        const editable = row.is_custom || row.auto_compute;
                                        if (!editable) return v == null ? "—" : v;
                                        return (
                                            <InputNumber
                                                value={v}
                                                onChange={(val) => updateLineField(modalOpenFor, idx, "unit_price", val)}
                                                step={0.01}
                                                precision={4}
                                                style={{ width: "100%" }}
                                                addonAfter={row.unit_is_percent ? "%" : null}
                                            />
                                        );
                                    }
                                },
                                {
                                    title: "Charge",
                                    dataIndex: "charge_amount",
                                    width: 130,
                                    render: (v, row, idx) => (
                                        <InputNumber
                                            value={v}
                                            onChange={(val) => updateLineAmount(modalOpenFor, idx, "charge_amount", val)}
                                            step={0.01}
                                            precision={2}
                                            style={{ width: "100%" }}
                                            disabled={false}
                                            placeholder={row.auto_compute ? "auto = qty × unit" : ""}
                                        />
                                    )
                                },
                                {
                                    title: "Credit",
                                    dataIndex: "credit_amount",
                                    width: 130,
                                    render: (v, _, idx) => (
                                        <InputNumber
                                            value={v}
                                            onChange={(val) => updateLineAmount(modalOpenFor, idx, "credit_amount", val)}
                                            step={0.01}
                                            precision={2}
                                            style={{ width: "100%" }}
                                        />
                                    )
                                },
                                {
                                    title: "",
                                    width: 70,
                                    render: (_, row, idx) => row.is_custom
                                        ? <Button danger size="small" onClick={() => removeLine(modalOpenFor, idx)}>Remove</Button>
                                        : null
                                }
                            ]}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Button onClick={() => addCustomLine(modalOpenFor)}>
                                + Add line (custom SKU)
                            </Button>
                            <Text type="secondary" style={{ marginLeft: 12 }}>
                                Use for one-off charges or credits not covered by the auto-computed lines.
                            </Text>
                        </div>
                        <div style={{ marginTop: 16, padding: 12, background: "#fafafa", borderRadius: 4 }}>
                            <Row>
                                <Col span={6}><Text>Sub Total (Credits)</Text></Col>
                                <Col span={6}><Text strong>{inr(editingDraft.totals.subTotalCredits)}</Text></Col>
                                <Col span={6}><Text>Invoice Amount</Text></Col>
                                <Col span={6}><Text strong>{inr(editingDraft.totals.invoiceAmount)}</Text></Col>
                            </Row>
                            <Row>
                                <Col span={6}><Text>Opening Balance</Text></Col>
                                <Col span={6}><Text strong>{inr(editingDraft.openingBalance)}</Text></Col>
                                <Col span={6}><Text>Net Settlement</Text></Col>
                                <Col span={6}>
                                    <Text strong style={{
                                        fontSize: 18,
                                        color: editingDraft.totals.netSettlement > 0
                                            ? "#52c41a"
                                            : editingDraft.totals.netSettlement < 0 ? "#f5222d" : undefined
                                    }}>
                                        {inr(editingDraft.totals.netSettlement)}
                                    </Text>
                                </Col>
                            </Row>
                            <Row><Col span={24}>
                                <Text type="secondary">
                                    Net positive = we pay operator. Negative = we collect from operator.
                                </Text>
                            </Col></Row>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
